<script setup>
import { computed, nextTick, onMounted, onBeforeUnmount, ref } from 'vue'
import Icon from '@/components/Icon.vue'
import { mdiTranslate, mdiChevronDown, mdiCheck } from '@mdi/js'
import { LANGUAGES, currentLanguage, setLanguage, initGoogleTranslate } from '@/globals/google-translate'
import { usePopover } from '@/composables/usePopover'

const props = defineProps({
  /* 'button' — the header control with its dropdown list (the default, and
     what the auth screens use). 'select' — a labelled native select, for the
     account menu on phones, where the top bar has no room for the button. */
  variant: {
    type: String,
    default: 'button'
  },
  /* Top bar only: the button shows the language CODE below xl and the full
     name from xl. "Bahasa Indonesia" next to the search box, the balance and
     the account name pushed the account button off the right edge at
     768–860px. Off by default so the auth screens keep their full label. */
  compact: Boolean
})

const isSelect = computed(() => props.variant === 'select')

const open = ref(false)
const active = ref(currentLanguage())
const root = ref(null)
const triggerRef = ref(null)
const panelRef = ref(null)

const activeLang = computed(
  () => LANGUAGES.find((l) => l.code === active.value) ?? LANGUAGES[0]
)

/*
 * TELEPORTED AND POSITIONED BY HAND, for the reason QuickAdjustPopover is.
 *
 * As a plain `absolute` child of the header this list was painted THROUGH by
 * the orders table: that table's header is `sticky` and its pinned actions
 * column is `sticky z-10`, and each of those establishes its own stacking
 * context in a later part of the document. A `z-50` set inside the header
 * cannot escape its own ancestor's context to beat them, so rows appeared over
 * the middle of the language list. Raising the number does not fix it; leaving
 * the containing block does.
 *
 * `PANEL_WIDTH` mirrors the `w-56` below — the panel has to be measured before
 * it can be clamped, and reading it back from the DOM would mean rendering it
 * off-screen first.
 */
const PANEL_WIDTH = 224
const MARGIN = 8
const GAP = 8

const position = ref({ top: 0, left: 0, maxHeight: 0 })

const panelStyle = computed(() => ({
  top: `${position.value.top}px`,
  left: `${position.value.left}px`,
  width: `${PANEL_WIDTH}px`,
  // Height is computed from the space actually below the trigger rather than a
  // flat `70vh`: anchored under a header near the top of a short window, 70vh
  // runs off the bottom and the last languages become unreachable again.
  maxHeight: `${position.value.maxHeight}px`,
}))

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max))

const place = () => {
  const trigger = root.value
  if (!trigger) return

  const rect = trigger.getBoundingClientRect()
  const vw = window.innerWidth
  const vh = window.innerHeight

  const top = rect.bottom + GAP

  position.value = {
    top,
    // Right-aligned to the trigger: it sits at the right end of the top bar, so
    // opening leftwards keeps the panel on screen.
    left: clamp(rect.right - PANEL_WIDTH, MARGIN, Math.max(MARGIN, vw - PANEL_WIDTH - MARGIN)),
    maxHeight: Math.max(160, vh - top - MARGIN),
  }
}

const close = ({ restoreFocus = false } = {}) => {
  if (!open.value) return
  open.value = false
  if (restoreFocus) triggerRef.value?.focus()
}

/* One header popover open at a time; focus into the list on open, and Tab past
   either end closes it. See composables/usePopover.js. Focus lands on the
   language already in use, which is where a listbox starts. */
const { panelId, onKeydown: onPopoverKeydown } = usePopover({
  name: 'language',
  isOpen: open,
  close,
  trigger: triggerRef,
  panel: panelRef,
  initialFocus: () => panelRef.value?.querySelector('[aria-selected="true"]') ?? null
})

/* Focus goes back to the trigger: choosing an option removes the list, and
   leaving focus on a node that no longer exists drops a keyboard user to
   <body>. No-op for the select variant, which has no list to close. */
const choose = (code) => {
  close({ restoreFocus: true })
  if (code === active.value) return
  active.value = code
  setLanguage(code)
}

const toggle = async () => {
  open.value = !open.value
  if (!open.value) return
  // Measured after the panel exists so a future change to its size is picked up.
  await nextTick()
  place()
}

/* Up/Down (and Home/End) walk the options, as they do in any listbox; Tab and
   Shift+Tab at the ends close the list (usePopover). */
const onPanelKeydown = (event) => {
  const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End']
  if (!keys.includes(event.key)) {
    onPopoverKeydown(event)
    return
  }
  const options = Array.from(panelRef.value?.querySelectorAll('[role="option"]') ?? [])
  if (!options.length) return
  event.preventDefault()
  const current = options.indexOf(document.activeElement)
  let next = 0
  if (event.key === 'End') next = options.length - 1
  else if (event.key === 'ArrowDown') next = current === -1 ? 0 : (current + 1) % options.length
  else if (event.key === 'ArrowUp') next = current <= 0 ? options.length - 1 : current - 1
  options[next].focus()
}

/* The panel is no longer a descendant of `root`, so a containment test against
   `root` alone would treat every click INSIDE the list as an outside click and
   close it before `choose` ran. Both nodes count. */
const onDocumentClick = (e) => {
  if (!open.value) return
  const inTrigger = root.value && root.value.contains(e.target)
  const inPanel = panelRef.value && panelRef.value.contains(e.target)
  if (!inTrigger && !inPanel) open.value = false
}

/* Escape returns focus to the trigger — but only from the list or the trigger
   itself, so an Escape pressed elsewhere does not pull focus into the header. */
const onKeydown = (e) => {
  if (e.key !== 'Escape' || !open.value) return
  const focused = document.activeElement
  const inside = (root.value && root.value.contains(focused)) ||
    (panelRef.value && panelRef.value.contains(focused))
  close({ restoreFocus: !!inside })
}

/* Reposition rather than dismiss: the top bar is sticky, so the trigger stays
   on screen while the page scrolls under it and closing would be gratuitous.
   Capture phase, because the scroll happens inside the table's own container
   rather than on the window. */
const onScrollOrResize = () => {
  if (open.value) place()
}

onMounted(() => {
  initGoogleTranslate()
  // The select variant has no list of its own to open, close or place.
  if (isSelect.value) return
  document.addEventListener('click', onDocumentClick)
  document.addEventListener('keydown', onKeydown)
  window.addEventListener('scroll', onScrollOrResize, true)
  window.addEventListener('resize', onScrollOrResize)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('keydown', onKeydown)
  window.removeEventListener('scroll', onScrollOrResize, true)
  window.removeEventListener('resize', onScrollOrResize)
})
</script>

<template>
  <label
    v-if="isSelect"
    class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300"
  >
    <!-- Phones: a native select inside the account menu. The option text is
         `notranslate` for the same reason as the list below. Google's widget
         container is not in either variant: App.vue renders the one
         #google_translate_element, see there.
         (Comments sit inside the roots: at the root, between v-if and v-else,
         they make a fragment in dev and the caller's class is dropped.) -->
    <icon :path="mdiTranslate" :size="18" w="w-5" h="h-5" class="flex-none" />
    <span class="flex-1">Language</span>
    <!-- Named explicitly: as a label's embedded control its name would
         otherwise be computed from the label text plus its own value. -->
    <select
      :value="active"
      aria-label="Language"
      class="notranslate w-36 rounded-lg border-gray-200 bg-white py-1 pl-2 text-sm text-gray-700 focus:border-brand-500 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
      translate="no"
      @change="choose($event.target.value)"
    >
      <option v-for="lang in LANGUAGES" :key="lang.code" :value="lang.code">
        {{ lang.flag }} {{ lang.name }}
      </option>
    </select>
  </label>
  <div v-else ref="root" class="relative notranslate" translate="no">
    <!-- `notranslate` keeps Google from translating the language names themselves,
         which would otherwise render "Türkçe" as "Turkish" once translated. -->
    <!-- `min-w/min-h` floors the hit area at the 44px touch minimum on phones
         without changing the 36px pill the desktop bar is laid out around. -->
    <button
      ref="triggerRef"
      type="button"
      class="inline-flex items-center justify-center gap-2 h-9 min-w-[2.75rem] min-h-[2.75rem] sm:min-w-0 sm:min-h-0 px-3 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
      :aria-expanded="open"
      :aria-controls="open ? panelId : undefined"
      aria-haspopup="listbox"
      :aria-label="`Change language — current: ${activeLang.name}`"
      @click.stop="toggle"
    >
      <icon :path="mdiTranslate" :size="18" w="w-5" h="h-5" class="text-gray-400 dark:text-gray-500" />
      <template v-if="compact">
        <span class="hidden xl:inline">{{ activeLang.flag }} {{ activeLang.name }}</span>
        <span class="xl:hidden uppercase">{{ activeLang.code }}</span>
      </template>
      <template v-else>
        <span class="hidden sm:inline">{{ activeLang.flag }} {{ activeLang.name }}</span>
        <span class="sm:hidden">{{ activeLang.flag }}</span>
      </template>
      <icon
        :path="mdiChevronDown"
        :size="16"
        w="w-4"
        h="h-4"
        class="text-gray-400 transition-transform duration-200"
        :class="{ 'rotate-180': open }"
      />
    </button>

    <!-- Teleported to <body>. See the note above `PANEL_WIDTH`: as a child of
         the header this list was painted through by the orders table's sticky
         header and pinned actions column, which sit in their own stacking
         contexts further down the document.

         `overflow-y-auto` rather than the `overflow-hidden` this used to carry:
         that was fine at seven languages and silently wrong at fifteen, because
         `hidden` clips the last entries with no way to reach them.
         `overscroll-contain` stops a flick at the end of the list from
         scrolling the page behind it. The height itself is measured in
         `place()`, not fixed in a class. -->
    <teleport to="body">
      <!-- NO <transition> HERE, deliberately.
           Choosing a language makes Google Translate rewrite the document, and
           that interrupted the leave transition: Vue never received the
           transitionend, so the panel stayed in the DOM at `opacity: 0` with
           `pointer-events: auto` — an invisible 224x601 click-trap at z-9999
           over the top-right of every page. Measured, not theorised.
           A fade on a dropdown is not worth a way to strand it. -->
      <ul
          v-if="open"
          :id="panelId"
          ref="panelRef"
          role="listbox"
          aria-label="Language"
          class="notranslate fixed z-[9999] w-56 overflow-y-auto overscroll-contain rounded-xl border border-gray-200 bg-white p-1.5 shadow-card-hover dark:border-gray-700 dark:bg-gray-800"
          translate="no"
          :style="panelStyle"
          @keydown="onPanelKeydown"
        >
        <li v-for="lang in LANGUAGES" :key="lang.code">
          <button
            type="button"
            role="option"
            :aria-selected="lang.code === active"
            class="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors"
            :class="lang.code === active
              ? 'bg-brand-50 font-semibold text-brand-700 dark:bg-brand-500 dark:bg-opacity-15 dark:text-brand-300'
              : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'"
            @click="choose(lang.code)"
          >
            <span class="text-base leading-none">{{ lang.flag }}</span>
            <span class="flex-grow text-left">{{ lang.name }}</span>
            <icon v-if="lang.code === active" :path="mdiCheck" :size="16" w="w-4" h="h-4" />
          </button>
        </li>

          <li class="mt-1 border-t border-gray-100 px-3 pt-2 pb-1 dark:border-gray-700">
            <p class="text-2xs leading-snug text-gray-400 dark:text-gray-500">
              Machine translation by Google Translate.
            </p>
          </li>
        </ul>
    </teleport>
  </div>
</template>
